import {rgb} from 'pdf-lib'
import routaDarkImage from '@/assets/routa_dark.jpg';

export class PdfGeneratorService {
    // Static variable to cache the image bytes
    static routaLogoBytes = null
    static companyLogoBytes = null

    constructor() {
        this.defaultVerticalMargin = 60
        this.defaultHorizontalMargin = 40
        this.defaultInnerHorizontalMargin = 10
        this.footerZone = 30 + this.defaultVerticalMargin
    }

    getHeaderColor() {
        return rgb(0, 0.21, 0.33)
        //return rgb(0.3725, 0.5686, 0)
    }

    getTextColor() {
        return rgb(0, 0, 0)
    }

    getBoldColor() {
        return rgb(0, 0.21, 0.33)
    }

    getHeaderLineColor() {
        return rgb(0.5, 0.76, 0)
    }

    /**
     * @param {PDFDocument} pdfDoc PDF document object
     * @param {string} text Text to add
     * @param {PDFFont} font Font object
     * @param {number} fontSize Font size
     * @param {number} pageY Y position to start drawing the title
     * @param {number} verticalMargin Vertical margin from the top of the page
     * @param {number} horizontalMargin Horizontal margin from the left of the page
     * @param {number} assumedContentZone Assumed min height of the content zone
     * @param {PDFImage} footerImage Footer image object
     * @returns {number} Y position after drawing the title
     */
    addText(pdfDoc, text, font, fontSize, pageY = undefined,
            verticalMargin = this.defaultVerticalMargin, horizontalMargin = this.defaultHorizontalMargin,
            assumedContentZone = 70, footerImage, footerCompanyLogo = null, color = rgb(0, 0, 0)) {
        const pages = pdfDoc.getPages()
        let page = pages[pages.length - 1]
        let y = pageY || page.getHeight() - verticalMargin

        // Start new page if content doesn't fit to current page
        const contentFit = () => {
            if (y && y < this.footerZone + assumedContentZone) {
                page = pdfDoc.addPage([595, 842])
                y = page.getHeight() - verticalMargin
                this.addRoutaLogoToFooter(pdfDoc, page, footerImage)
                this.addCompanyLogoToFooter(pdfDoc, page, footerCompanyLogo)
            }
        }
        contentFit()

        const width = page.getWidth() - 2 * horizontalMargin
        const x = horizontalMargin


        const lines = this.getWrappedText(font, text.toString(), fontSize, width)
        lines.forEach((lineText) => {
            page.drawText(lineText, {
                x: x,
                y: y,
                size: fontSize,
                font: font,
                color: color,
            })

            y -= fontSize + 2
            contentFit()
        })

        return y - 5
    }

    addTextWithIcon(
        pdfDoc,
        text,
        icon,
        font,
        fontSize,
        pageY = undefined,
        verticalMargin = this.defaultVerticalMargin,
        horizontalMargin = this.defaultHorizontalMargin,
        assumedContentZone = 70,
        footerImage,
        footerCompanyLogo = null,
        color = rgb(0, 0, 0),
        iconWidth = 25,
        iconHeight = 25,
        iconSpacing = 8
    ) {
        const pages = pdfDoc.getPages()
        let page = pages[pages.length - 1]
        let y = pageY || page.getHeight() - verticalMargin

        // Start new page if content doesn't fit to current page
        const contentFit = () => {
            if (y && y < this.footerZone + assumedContentZone) {
                page = pdfDoc.addPage([595, 842])
                y = page.getHeight() - verticalMargin

                this.addRoutaLogoToFooter(
                    pdfDoc,
                    page,
                    footerImage
                )

                this.addCompanyLogoToFooter(
                    pdfDoc,
                    page,
                    footerCompanyLogo
                )
            }
        }

        contentFit()

        const x = horizontalMargin

        // Text has slightly less space because the icon uses space on the left
        const width =
            page.getWidth() -
            2 * horizontalMargin -
            iconWidth -
            iconSpacing

        const lines = this.getWrappedText(
            font,
            text.toString(),
            fontSize,
            width
        )

        // Draw icon alongside the first text line
        if (icon) {
            page.drawImage(icon, {
                x: x,
                y: y - (iconHeight - fontSize) / 2,
                width: iconWidth,
                height: iconHeight
            })
        }

        const textX = x + iconWidth + iconSpacing

        lines.forEach((lineText) => {
            page.drawText(lineText, {
                x: textX,
                y: y,
                size: fontSize,
                font: font,
                color: color,
            })

            y -= fontSize + 2
            contentFit()
        })

        // If the icon is taller than the text, make sure following
        // content starts below the icon.
        const textHeight = lines.length * (fontSize + 2)

        if (iconHeight > textHeight) {
            y -= iconHeight - textHeight
        }

        return y - 5
    }

    /**
     * @param {Array} tableData Multi-dimensional array containing the table data
     * @param {PDFDocument} pdfDoc PDF document object
     * @param fontStyles
     * @param {number} pageY Y position to start drawing the table
     * @param {number} verticalMargin Vertical margin from the top of the page
     * @param {number} horizontalMargin Horizontal margin from the left of the page
     * @param {PDFImage} footerImage object
     * @param itemsPerRow
     * @param index
     * @param rowPadding
     * @param slots How many slots this item takes from items per row
     * @param footerCompanyLogo
     */
    // eslint-disable-next-line
    addVerticalTable(tableData, pdfDoc, fontStyles, pageY = undefined, verticalMargin = this.defaultVerticalMargin,
                     horizontalMargin = this.defaultHorizontalMargin, footerImage, itemsPerRow = 1,
                     index = 1, rowPadding = 10, slots = 1, footerCompanyLogo = null) {
        const pages = pdfDoc.getPages()
        let page = pages[pages.length - 1]

        const componentStart = this.getComponentStart(page, itemsPerRow, index)

        // Table styling and layout
        const tableWidth = this.getComponentLength(page, itemsPerRow, slots)
        const numColumns = 2
        const columnWidth = tableWidth / numColumns
        const font = fontStyles.textFont
        const headerFont = fontStyles.headerFont || font
        const defaultFontSize = fontStyles.textFontSize || 12
        const defaultHeaderFontSize = fontStyles.headerFontSize || defaultFontSize
        const textColor = fontStyles.textColor ? fontStyles.textColor : rgb(0, 0, 0)
        const headerColor = fontStyles.headerColor ? fontStyles.headerColor : rgb(0, 0, 0)

        // Y position to start drawing the table
        let y = pageY || page.getHeight() - verticalMargin

        const addText = (lineText, x, y, textFont, fontSize, color = rgb(0, 0,0 )) => {
            page.drawText(lineText, {
                x: x,
                y: y,
                size: fontSize,
                font: textFont,
                color: color,
            })
        }

        for (const row of tableData) {
            let x = componentStart
            let rowHeight = 20

            // Check that the text fit to the current page
            if (y - rowHeight < this.footerZone) {
                page = pdfDoc.addPage([595, 842])
                this.addRoutaLogoToFooter(pdfDoc, page, footerImage)
                this.addCompanyLogoToFooter(pdfDoc, page, footerCompanyLogo)
                y = page.getHeight() - verticalMargin
            }

            // Row should only have two columns, "header" and "value"
            row.forEach((cellValue, index) => {
                let cellText = cellValue !== null && cellValue !== undefined ? cellValue.toString() : '-'
                const lines = this.getWrappedText(font, cellText, defaultFontSize, columnWidth)
                rowHeight = Math.max(rowHeight, lines.length * (defaultFontSize + 2) + 2 * rowPadding)

                let textY = y - rowPadding
                lines.forEach((lineText) => {
                    const lineFontSize = index === 0 ? defaultHeaderFontSize : defaultFontSize
                    const lineFont = index === 0 ? headerFont : font
                    addText(lineText, x + 5, textY - defaultFontSize, lineFont, lineFontSize, index === 0 ? headerColor : textColor)
                    textY -= defaultFontSize + 2
                })

                x += columnWidth
            })

            // Draw a horizontal line at the end of the row
            const lineColor = rgb(0.7, 0.7, 0.7)
            page.drawLine({
                start: {x: componentStart, y: y - rowHeight},
                end: {x: componentStart + tableWidth, y: y - rowHeight},
                thickness: 0,
                color: lineColor,
            })

            y -= rowHeight
        }

        return y
    }



    addHorizontalLine(pdfDoc, pageY = undefined, verticalMargin = this.defaultVerticalMargin, footerImage, itemsPerRow = 1, index = 1) {
        const pages = pdfDoc.getPages()
        let page = pages[pages.length - 1]

        const componentStart = this.getComponentStart(page, itemsPerRow, index)
        const componentEnd = componentStart + this.getComponentLength(page, itemsPerRow, index)

        // Y position to start drawing the table
        let y = pageY || page.getHeight() - verticalMargin

        // Draw a horizontal line at the end of the row
        const lineColor = rgb(0.5, 0.5, 0.5)
        page.drawLine({
            start: {x: componentStart, y: y},
            end: {x: componentEnd, y: y},
            thickness: 0,
            color: lineColor,
        })

        y -= 10

        return y
    }

    /**
     * @param {Array} tableData Multi-dimensional array containing the table data
     * @param {PDFDocument} pdfDoc PDF document object
     * @param fontStyles
     * @param {number} pageY Y position to start drawing the table
     * @param {number} verticalMargin Vertical margin from the top of the page
     * @param {number} horizontalMargin Horizontal margin from the left of the page
     * @param {PDFImage} footerImage object
     * @param itemsPerRow Total number of components on line
     * @param index A number from 1 to `amount`
     */
    // eslint-disable-next-line
    addTable(tableData, pdfDoc, fontStyles, pageY = undefined, verticalMargin = this.defaultVerticalMargin,
             horizontalMargin = this.defaultHorizontalMargin, footerImage, itemsPerRow = 1, index = 1, landscape = false, companyFooterLogo = null) {
        const pages = pdfDoc.getPages()
        let page = pages[pages.length - 1]

        // Table styling and layout
        let componentStart = this.getComponentStart(page, itemsPerRow, index)

        const tableWidth = this.getComponentLength(page, itemsPerRow, 1)
        const numColumns = tableData.length > 1 ? tableData[1].length : tableData[0].length // Header can be empty
        const columnWidth = tableWidth / numColumns
        const rowPadding = 5 // Padding above and below each row
        const font = fontStyles.textFont
        const headerFont = fontStyles.headerFont || font
        const defaultFontSize = fontStyles.textFontSize || 12
        const defaultHeaderFontSize = fontStyles.headerFontSize || defaultFontSize
        const textColor = fontStyles.textColor ? fontStyles.textColor : rgb(0, 0, 0)
        const headerColor = fontStyles.headerFontColor ? fontStyles.headerFontColor : rgb(0, 0, 0)
        const lineColor = rgb(0.7, 0.7, 0.7)

        // Y position to start drawing the table
        let y = pageY || page.getHeight() - verticalMargin

        // Slice the first row of the table data to get the headers
        const headerData = tableData.length > 0 ? tableData.slice(0, 1)[0] : []
        const bodyData = tableData.length > 1 ? tableData.slice(1) : []

        const addText = (lineText, x, y, textFont, fontSize, color = rgb(0, 0, 0)) => {
            page.drawText(lineText, {
                x: x,
                y: y,
                size: fontSize,
                font: textFont,
                color: color,
            })
        }

        const addHeaders = (headers, font, x, y) => {
            let headerX = x
            let headerY = y
            let maxHeaderLines = 1

            headers.forEach((text) => {
                let headerText = text !== null && text !== undefined ? text.toString() : '-'
                const lines = this.getWrappedText(font, headerText, defaultFontSize, columnWidth)
                maxHeaderLines = Math.max(maxHeaderLines, lines.length)
                lines.forEach((lineText, lineIndex) => {
                    addText(lineText, headerX + 5, headerY - (defaultFontSize + 2) * lineIndex - 5, headerFont, defaultHeaderFontSize, headerColor)
                })
                headerX += columnWidth
            })


            // Adjust Y position after drawing the headers based on the tallest header cell
            y = y - (maxHeaderLines * (defaultFontSize + 2))
            // Draw a horizontal line at the end of the row
            page.drawLine({
                start: {x: componentStart, y: y},
                end: {x: componentStart + tableWidth, y: y},
                thickness: 0,
                color: this.getHeaderLineColor()
            })
            return y - 10
        }

        // Draw headers on the first page
        if (headerData.length > 0) {
            y = addHeaders(headerData, font, componentStart, y)
        }

        for (const row of bodyData) {
            let rowHeight = 20

            /*
             * Calculate the row height before checking page overflow.
             */
            for (const cellValue of row) {
                const isImageCell =
                    cellValue &&
                    typeof cellValue === 'object' &&
                    cellValue.type === 'image' &&
                    cellValue.image

                if (isImageCell) {
                    const imageHeight = cellValue.height || 25

                    rowHeight = Math.max(
                        rowHeight,
                        imageHeight + 2 * rowPadding
                    )
                } else {
                    const cellText =
                        cellValue !== null && cellValue !== undefined
                            ? cellValue.toString()
                            : '-'

                    const lines = this.getWrappedText(
                        font,
                        cellText,
                        defaultFontSize,
                        columnWidth - 10
                    )

                    rowHeight = Math.max(
                        rowHeight,
                        lines.length * (defaultFontSize + 2) +
                        2 * rowPadding
                    )
                }
            }

            /*
             * Add another page if the complete row does not fit.
             */
            if (y - rowHeight < this.footerZone) {
                page = landscape
                    ? pdfDoc.addPage([842, 595])
                    : pdfDoc.addPage()

                this.addRoutaLogoToFooter(
                    pdfDoc,
                    page,
                    footerImage
                )

                this.addCompanyLogoToFooter(
                    pdfDoc,
                    page,
                    companyFooterLogo
                )

                y = page.getHeight() - verticalMargin

                if (headerData.length > 0) {
                    y = addHeaders(
                        headerData,
                        font,
                        componentStart,
                        y
                    )
                }
            }

            let x = componentStart

            for (const cellValue of row) {
                const isImageCell =
                    cellValue &&
                    typeof cellValue === 'object' &&
                    cellValue.type === 'image' &&
                    cellValue.image

                if (isImageCell) {
                    const requestedWidth = cellValue.width || 25
                    const requestedHeight = cellValue.height || 25
                    const availableWidth = columnWidth - 10

                    const scale = Math.min(
                        1,
                        availableWidth / requestedWidth
                    )

                    const imageWidth = requestedWidth * scale
                    const imageHeight = requestedHeight * scale

                    const imageY =
                        y -
                        rowHeight +
                        (rowHeight - imageHeight) / 2

                    page.drawImage(cellValue.image, {
                        x: x + 5,
                        y: imageY,
                        width: imageWidth,
                        height: imageHeight
                    })
                } else {
                    const cellText =
                        cellValue !== null && cellValue !== undefined
                            ? cellValue.toString()
                            : '-'

                    const lines = this.getWrappedText(
                        font,
                        cellText,
                        defaultFontSize,
                        columnWidth - 10
                    )

                    let textY = y - rowPadding

                    lines.forEach(lineText => {
                        addText(
                            lineText,
                            x + 5,
                            textY - defaultFontSize,
                            font,
                            defaultFontSize,
                            textColor
                        )

                        textY -= defaultFontSize + 2
                    })
                }

                x += columnWidth
            }

            page.drawLine({
                start: {
                    x: componentStart,
                    y: y - rowHeight
                },
                end: {
                    x: componentStart + tableWidth,
                    y: y - rowHeight
                },
                thickness: 0,
                color: lineColor
            })

            y -= rowHeight
        }

        return y
    }

    /*
    * Calculate text width and wrap text
    * @param {PDFFont} font Font object
    * @param {string} text Text to wrap
    * @param {number} fontSize Font size
    * @param {number} maxWidth Maximum width of the text
    * @returns {Array} Array of wrapped text lines
    */
    getWrappedText(font, text, fontSize, maxWidth) {
        text = text.replaceAll(/\n/g, ' ')
        text = text.replaceAll(/\r/g, ' ')
        const words = text.split(' ')
        let lines = []
        let line = ''

        // Split the text into lines that fit within the column width
        for (const word of words) {
            const testLine = line + word + ' '
            const textWidth = font.widthOfTextAtSize(testLine, fontSize)
            if (textWidth > maxWidth - 10) { // If the word does not fit
                if (line.length > 0) {
                    lines.push(line.trim()) // Add the current line to the lines array
                }
                line = word + ' ' // Start a new line with the current word
            } else {
                line = testLine // Continue the current line with the word
            }
        }

        // Handle single long words without spaces
        if (line.length > 0) {
            const wordWidth = font.widthOfTextAtSize(line, fontSize)
            if (wordWidth > maxWidth - 10) {
                // Word is too long, break it into smaller chunks
                let start = 0
                while (start < line.length) {
                    let end = start + Math.floor(maxWidth / font.widthOfTextAtSize('A', fontSize))
                    lines.push(line.slice(start, end))
                    start = end
                }
            } else {
                lines.push(line.trim())
            }
        }

        return lines
    }

    static async cacheRoutaFooterLogo() {
        // Check if we already have the image bytes cached
        if (!PdfGeneratorService.routaLogoBytes) {
            const imagePath = routaDarkImage;
            // Fetch image bytes and store in the static variable if not already cached
            // We can also use browser cache to store the image bytes
            PdfGeneratorService.routaLogoBytes = await fetch(imagePath)
                .then((res) => res.arrayBuffer())
                .catch(() => null)
        }
    }

    /**
     * @param {PDFDocument} pdfDoc document object
     * @param {ArrayBuffer} footerImageBytes Image bytes for the footer logo
     * @returns {PDFImage} Image object
     */
    static async embedImageToDoc(pdfDoc, footerImageBytes) {
        if (!footerImageBytes) {
            return
        }

        const image = await pdfDoc.embedJpg(footerImageBytes)
        return image
    }

    /**
     * @param {PDFDocument} pdfDoc document object
     * @param imageDataUrl Image base64 bytes
     * @returns {PDFImage} Image object
     */
    static async embedAnyImageToDoc(pdfDoc, imageDataUrl) {
        if (!imageDataUrl) {
            return;
        }

        // Extract MIME type from data URL
        const matches = imageDataUrl.match(/^data:(image\/(png|jpeg));base64,/);
        if (!matches) {
            throw new Error("Unsupported or invalid image format.");
        }

        const mimeType = matches[1]; // "image/png" or "image/jpeg"
        const base64Data = imageDataUrl.replace(/^data:image\/(png|jpeg);base64,/, '');
        const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));

        if (mimeType === "image/png") {
            return await pdfDoc.embedPng(imageBytes);
        } else if (mimeType === "image/jpeg") {
            return await pdfDoc.embedJpg(imageBytes);
        } else {
            throw new Error("Only PNG and JPEG images are supported.");
        }
    }

    /**
     *
     * @param {PDFDocument} pdfDoc PDF document object
     * @param {ArrayBuffer} fontBytes Font bytes
     * @returns {PDFFont} Font object
     */
    async embedFontToDoc(pdfDoc, fontBytes) { //
        const font = await pdfDoc.embedFont(fontBytes)
        return font
    }

    /**
     * @param {PDFDocument} pdfDoc document object
     * @param page
     * @param {PDFImage} embededImage Image object
     */
    addRoutaLogoToFooter(pdfDoc, page = null, embededImage) {
        if (!embededImage) {
            return
        }

        let currenPage = page
        if (!currenPage) {
            const pages = pdfDoc.getPages()
            currenPage = pages[pages.length - 1]
        }

        const imageHeight = 20;
        const imageWidth = (embededImage.width / embededImage.height) * imageHeight;
        const pageWidth = page.getWidth()

        page.drawImage(embededImage, {
            x: pageWidth - imageWidth - 50, // Align to the right with a margin
            y: 40,
            width: imageWidth,
            height: imageHeight,
        })
    }

    /**
     * @param {PDFDocument} pdfDoc document object
     * @param page
     * @param {PDFImage} embededImage Image object
     */
    addCompanyLogoToFooter(pdfDoc, page = null, embededImage) {
        if (!embededImage) {
            return;
        }

        let currenPage = page;
        if (!currenPage) {
            const pages = pdfDoc.getPages();
            currenPage = pages[pages.length - 1];
        }

        const imageHeight = 40;
        const imageWidth = (embededImage.width / embededImage.height) * imageHeight;

        // Set x to a small margin value for left alignment
        const xPosition = 50; // Adjust this value to your preferred left margin

        currenPage.drawImage(embededImage, {
            x: xPosition,
            y: 30,
            width: imageWidth,
            height: imageHeight,
        });
    }


    /**
     * @param {PDFDocument} pdfDoc document object
     * @param page
     * @param embededImages
     * @param x
     * @param y
     * @param {PDFImage} footerImage object
     * @param itemsPerRow
     * @param index
     * @param imagesPerRow
     * @param verticalRowAmount
     * @param slots
     * @param footerCompanyLogo
     */
    // eslint-disable-next-line
    addImages(pdfDoc, page = null, embededImages = [], x = null, y = null, footerImage, itemsPerRow = 1, index = 1, imagesPerRow = 2, verticalRowAmount = 3, slots = 1, footerCompanyLogo = null) {

        const defaultVerticalMargin = 5

        embededImages.forEach((image, i) => {
            let currenPage = page
            if (!currenPage) {
                const pages = pdfDoc.getPages()
                currenPage = pages[pages.length - 1]
            }

            const defaultHeight = this.getDefaultPageHeight(currenPage)

            const requiredImageHeight = (defaultHeight / verticalRowAmount) - (defaultVerticalMargin * verticalRowAmount) + 80

            const maxWidth = this.getComponentLength(currenPage, itemsPerRow, slots)

            const imageSize = this.scaleImageWidth(maxWidth, imagesPerRow)

            const jpgDims = image.scaleToFit(imageSize, requiredImageHeight)

            if (this.isPartOfSequence(i, imagesPerRow)) {
                y -= (requiredImageHeight + defaultVerticalMargin)
            }

            if (y < this.footerZone) {
                page = pdfDoc.addPage([595, 842])
                this.addRoutaLogoToFooter(pdfDoc, page, footerImage)
                this.addCompanyLogoToFooter(pdfDoc, page, footerCompanyLogo)
                currenPage = page
                y = page.getHeight() - requiredImageHeight - defaultVerticalMargin
            }

            const start = this.getComponentStart(currenPage, itemsPerRow, index)

            currenPage.drawImage(image, {
                x: this.getForImageXPosition(maxWidth, i, imagesPerRow, imageSize, start),
                y: y,
                width: jpgDims.width,
                height: jpgDims.height,
            })
        })
        return y
    }




    /**
     * @param {PDFDocument} pdfDoc document object
     * @param page
     * @param embededImage
     * @param x
     * @param y
     * @param {PDFImage} footerImage object
     * @param lessY
     * @param itemPerRow
     * @param index
     * @param footerCompanyLogo
     * @param slots
     * @param observationMapHeight
     * @param centerImage
     */
    // eslint-disable-next-line
    addImage(pdfDoc, page = null, embededImage, x = null,
        y = null, footerImage, lessY = 0,
        itemPerRow = 1, index = 1, slots = 1,
        footerCompanyLogo = null, observationMapHeight = 0,
        centerImage = false
    ) {
        let currenPage = page
        if (!currenPage) {
            const pages = pdfDoc.getPages()
            currenPage = pages[pages.length - 1]
        }
        let width = this.getComponentLength(currenPage, itemPerRow, slots)

        let jpgDims;

        if (observationMapHeight) {
            jpgDims = { width: width, height: observationMapHeight }
        } else {
            jpgDims = embededImage.scaleToFit(width, width - lessY)
        }
        if (y - jpgDims.height < this.footerZone) {
            page = pdfDoc.addPage([595, 842])
            this.addRoutaLogoToFooter(pdfDoc, page, footerImage)
            this.addCompanyLogoToFooter(pdfDoc, page, footerCompanyLogo)
            currenPage = page
            y = page.getHeight() - this.defaultVerticalMargin

        }
        if (!x) {
            x = this.getComponentStart(currenPage, itemPerRow, index) + (index > 1 ? this.defaultInnerHorizontalMargin : 0)
        }

        const startX = this.getComponentStart(currenPage, itemPerRow, index)

        // Draw gray background
        if (centerImage) {
            currenPage.drawRectangle({
                x: startX,
                y: y - jpgDims.height,
                width: width,
                height: jpgDims.height,
                color: rgb(0.9, 0.9, 0.9)
            })
            x = startX + (width - jpgDims.width) / 2
        } else if (!x) {
            x = startX + (index > 1 ? this.defaultInnerHorizontalMargin : 0)
        }

        y -= jpgDims.height
        currenPage.drawImage(embededImage, {
            x: x,
            y: y,
            width: jpgDims.width,
            height: jpgDims.height,
        })
        return y
    }

    getForImageXPosition(width, index, lineAmount, imageWidth, start) {
        const imageWidths = width / lineAmount;
        const column = index % lineAmount;
        return start + column * imageWidths;
    }

    getComponentLength(page, amount, slots) {
        const pageWidth = page.getWidth()
        const outerMargin = this.defaultHorizontalMargin;
        const availableSpace = pageWidth - ( outerMargin * 2 )
        return availableSpace * (slots / amount)
    }

    getComponentStart(page, amount, index) {
        const innerMargin = this.defaultInnerHorizontalMargin
        const pageWidth = page.getWidth()
        const outerMargin = this.defaultHorizontalMargin;
        const availableSpace = pageWidth - ( outerMargin * 2 )
        return outerMargin + availableSpace * ((index-1)/amount)
        //return outerMargin + (componentWidth * (index - 1)) + innerMargin * (index - 1)
    }

    scaleImageWidth(availableWidth, amount, imageMargin = 10) {
        const totalMargin = (amount - 1) * imageMargin;
        return (availableWidth - totalMargin) / amount;
    }

    isPartOfSequence(number, sequence) {
        return number % sequence === 0;
    }

    getBlankPageHeight(page) {
        return page.getHeight() - this.defaultVerticalMargin
    }

    getDefaultPageHeight(page) {
        return page.getHeight() - this.defaultVerticalMargin - this.footerZone
    }

    getAvailablePageHeight(page, currentY) {
        return page.getHeight() - this.footerZone - currentY
    }

}
